import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    Icon,
    IconButton,
    Input, Menu, MenuButton, MenuDivider, MenuItem, MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Table,
    Tag,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
} from '@chakra-ui/react';
import {MdAddCall, MdCheckCircle, MdDelete, MdEdit, MdVisibility, MdWarning} from 'react-icons/md';
import {CheckIcon, WarningIcon} from '@chakra-ui/icons';
import axiosInstance from '../../../api/axiosInstance';
import dayjs from 'dayjs';
import Pagination from '../orders/Pagination';
import ConfirmationDialog from '../orders/ConfirmationDialog';
import MultiSelectDropdown from '../../../components/multiselect/MultiSelectDropdown';
import Card from 'components/card/Card';
import useHasPermission from "../../../hooks/useHasPermission";
import {useNotification} from "../../../contexts/NotificationContext";
import {HiOutlineDotsVertical} from "react-icons/hi";

const PartialOrdersPage = () => {
    const [partialOrders, setPartialOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalOrders, setTotalOrders] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [shouldSearch, setShouldSearch] = useState(0);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedProductName, setSelectedProductName] = useState('All products');
    const [submitAttemptFilter, setSubmitAttemptFilter] = useState('');
    const [products, setProducts] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [excludedProducts, setExcludedProducts] = useState([]);
    const [chips, setChips] = useState([]);
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [orderToDelete, setOrderToDelete] = useState(null);
    const hasPermission = useHasPermission()
    const canDeletePartials = hasPermission('delete-partials')
    const canEditPartials = hasPermission('edit-partials')
    const isPremium = hasPermission('premium')
    const {addErrorNotifications, addNotification} = useNotification()
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [editPartial, setEditPartial] = useState(null)
    const [editForm, setEditForm] = useState({
        option_id: '',
        name: '',
        phone: '',
        county: '',
        city: '',
        address: '',
        upsells: [],
        colors: [],
        color_ids: [],
    })

    useEffect(() => {
        fetchPartialOrders();
    }, [currentPage, shouldSearch, excludedProducts, selectedProduct, submitAttemptFilter]);

    useEffect(() => {
        if (!editPartial) return

        const option = editPartial.product.options.find(
            o => String(o.id) === String(editForm.option_id)
        )
        const count = option?.quantity || 0

        setEditForm(prev => {
            const existing = prev.color_ids || []
            const next = existing.slice(0, count)
            while (next.length < count) next.push('')
            return {
                ...prev,
                color_ids: next
            }
        })
    }, [
        editForm.option_id,
        editPartial
    ])

    const fetchPartialOrders = async () => {
        try {
            const response = await axiosInstance.get('/reports/partial-orders', {
                headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
                params: {
                    page: searchTerm ? 1 : currentPage,
                    searchTerm: searchTerm,
                    product_id: selectedProduct,
                    excluded_ids: excludedProducts,
                    submit_attempt: submitAttemptFilter,
                },
            });

            setPartialOrders(response.data.orders);
            setTotalPages(response.data.total_pages);
            setTotalOrders(response.data.order_count);
            setProducts(response.data.products);

            if (chips.length === 0) {
                setChips(response.data.products);
            }
        } catch (e) {
            addErrorNotifications(e?.response?.data)
        }
    };

    const handlePageChange = (value) => {
        setCurrentPage(value);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        if (event.target.value === '') {
            setShouldSearch((prev) => prev + 1);
        }
    };

    const handleSearchDatabase = (event) => {
        if (event.key === 'Enter') {
            setShouldSearch((prev) => prev + 1);
        }
    };

    const handleSelectProduct = (event) => {
        const productId = event.target.value;
        setSelectedProduct(productId);
        setSelectedProductName(products.find(product => product.id === Number(productId))?.name || 'All products')
        setCurrentPage(1)
    };

    const handleSubmitAttemptFilter = (event) => {
        setSubmitAttemptFilter(event.target.value)
        setCurrentPage(1)
    };

    const handleSelectOrder = (orderId) => {
        setSelectedOrders((prevSelectedOrders) =>
            prevSelectedOrders.includes(orderId)
                ? prevSelectedOrders.filter((id) => id !== orderId)
                : [...prevSelectedOrders, orderId]
        );
    };

    const handleSelectAllOrders = (event) => {
        if (event.target.checked) {
            const allOrderIds = partialOrders.map((order) => order.id);
            setSelectedOrders(allOrderIds);
        } else {
            setSelectedOrders([]);
        }
    };

    const handleDeleteSelected = async () => {
        try {
            await axiosInstance.post('/reports/partial-orders/bulk-delete', {orderIds: selectedOrders}, {
                headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            });
            addNotification('Partials deleted!', 'success')
            fetchPartialOrders();
            setSelectedOrders([]);
        } catch (error) {
            addErrorNotifications(error)
        }
    };

    const handleAddCall = async (order) => {
        try {
            await axiosInstance.get(`/reports/partial-orders/add-call/${order.id}`, {
                headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            });
            addNotification('Call added!', 'success')
            fetchPartialOrders();
        } catch (error) {
            addErrorNotifications(error)
        }
    };

    const handleClickOpen = (order) => {
        setOrderToDelete(order);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        if (orderToDelete) {
            try {
                await axiosInstance.delete(`/reports/partial-orders/${orderToDelete.id}`, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
                });
                addNotification('Partial deleted!', 'success')
                fetchPartialOrders();
                onClose();
            } catch (error) {
                addErrorNotifications(error)
            }
        }
    };

    const buildPartialName = (order) => {
        let name = `${order.option?.name}`;
        if (order.colors && order.colors.length > 0) {
            const colorList = order.colors.join(', ');
            name += ` (${colorList})`;
        }
        return <Box dangerouslySetInnerHTML={{__html: name}}/>;
    };

    const handleConfirmOrder = async (order) => {
        if (order.phone?.length < 10) {
            addErrorNotifications({error: 'The phone number should have at least 10 digits!'})
            return
        }

        try {
            await axiosInstance.post(
                `/reports/partial-orders/${order.id}/confirm`,
                null,
                {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}}
            );
            addNotification('Order confirmed!', 'success')
            fetchPartialOrders();
        } catch (error) {
            addErrorNotifications(error?.response?.data || error);
        }
    };

    const openEditPartial = (order) => {
        const data = typeof order.data === 'string' ? JSON.parse(order.data) : order.data
        setEditPartial(order)
        setEditForm({
            option_id: data.option_id || '',
            name: data.name || '',
            phone: data.phone || '',
            county: data.county || '',
            city: data.city || '',
            address: data.address || '',
            upsells: order?.upsells || [],
            color_ids: extractColorsFromData(order, data.option_id),
        })
        setIsEditModalOpen(true)
    }

    const handleEditFormChange = (e) => {
        setEditForm({
            ...editForm,
            [e.target.name]: e.target.value
        })
    }

    const handleSaveEditPartial = async () => {
        if (editForm?.phone?.length < 10) {
            addErrorNotifications({error: 'The phone number should have at least 10 digits!'})
            return
        }

        const {color_ids = [], option_id, upsells = [], ...rest} = editForm

        const colorPayload = color_ids.reduce((acc, colorId, idx) => {
            acc[`color_${option_id}_${idx}`] = colorId
            return acc
        }, {})

        const upsellPayload = upsells.reduce((acc, upsell) => {
            const key = `upsell_${upsell.id}_${upsell.upsell_id}`
            acc[key] = editPartial?.[`upsell_${upsell.id}_${upsell.upsell_id}`]
                ?? upsell.price
                ?? ''
            return acc
        }, {})

        const payload = {
            ...rest,
            option_id,
            ...colorPayload,
            ...upsellPayload
        }

        try {
            await axiosInstance.put(`/reports/partial-orders/${editPartial.id}`, {data: payload}, {
                headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
            })
            addNotification('Partial updated successfully!', 'success')
            setIsEditModalOpen(false)
            fetchPartialOrders()
        } catch (error) {
            addErrorNotifications(error?.response?.data || error)
        }
    }

    const extractColorsFromData = (data, optionId) => {
        let colors = []
        Object.keys(data).forEach(key => {
            if (key.startsWith(`color_${optionId}_`)) {
                colors.push(data[key])
            }
        })
        return colors
    }

    return (
        <Box pt={{base: "130px", md: "80px", xl: "80px"}} maxWidth="xxl" px={{base: '20px', md: '40px'}}>

            <Card mb="20px" px={{base: 4, md: 6}}>
                <SimpleGrid columns={{base: 1, md: 2, lg: 3, xl: 4}} spacing="20px">
                    <FormControl>
                        <FormLabel>Product</FormLabel>
                        <Select placeholder="Select Product" w="100%" borderRadius="md" onChange={handleSelectProduct}>
                            {products.map((product) => (
                                <option key={product.id} value={product.id}>
                                    {product.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Search</FormLabel>
                        <Input
                            placeholder="Search"
                            w="100%"
                            borderRadius="md"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchDatabase}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Submit Attempt</FormLabel>
                        <Select value={submitAttemptFilter} onChange={handleSubmitAttemptFilter}>
                            <option value="">All</option>
                            <option value="attempted">Attempted</option>
                            <option value="not_attempted">Not attempted</option>
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Exclude products from list</FormLabel>
                        <MultiSelectDropdown
                            options={chips}
                            selectedOptions={excludedProducts}
                            setSelectedOptions={setExcludedProducts}
                        />
                    </FormControl>

                    {selectedOrders.length > 0 && (
                        <Flex justifyContent="flex-end" alignItems="end">
                            <Tooltip label="Delete selected partials">
                                <Button colorScheme="red" leftIcon={<Icon as={MdDelete}/>}
                                        onClick={handleDeleteSelected}>
                                    Delete
                                </Button>
                            </Tooltip>
                        </Flex>
                    )}
                </SimpleGrid>
            </Card>

            <Card>
                <Box overflowX="auto">
                    <Box display="flex" justifyContent="space-between" mb="4" p="4">
                        <Text fontSize="2xl" fontWeight="bold">
                            {selectedProductName || 'All products'} - {totalOrders}
                        </Text>
                    </Box>

                    <Table variant="simple" size="sm" boxShadow="lg" rounded="md">
                        <Thead>
                            <Tr>
                                <Th>
                                    <Checkbox
                                        isChecked={selectedOrders.length === partialOrders.length}
                                        isIndeterminate={selectedOrders.length > 0 && selectedOrders.length < partialOrders.length}
                                        onChange={handleSelectAllOrders}
                                    />
                                </Th>
                                <Th>ID</Th>
                                <Th>Created At</Th>
                                <Th>Product</Th>
                                <Th>Option</Th>
                                <Th>Name</Th>
                                <Th>Phone</Th>
                                <Th>County</Th>
                                <Th>City</Th>
                                <Th>Address</Th>
                                <Th>Shipment</Th>
                                <Th>Submit Attempt</Th>
                                <Th>Phone Exists</Th>
                                <Th>Terms</Th>
                                <Th>Number Of Calls</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {partialOrders.map((order) => (
                                <Tr
                                    key={order.id}
                                    bg={selectedOrders.includes(order.id) ? '#f7f7f7' : 'transparent'}
                                    transition="background-color 0.18s ease"
                                    _hover={{ bg: '#f7f7f7' }}
                                >
                                    <Td>
                                        <Checkbox
                                            isChecked={selectedOrders.includes(order.id)}
                                            onChange={() => handleSelectOrder(order.id)}
                                        />
                                    </Td>
                                    <Td>{order.id}</Td>
                                    <Td>{dayjs(order.created_at).format('DD MMMM YYYY HH:mm')}</Td>
                                    <Td>{`${order.quantity} x ${order.product?.name} - ${order.price}`}</Td>
                                    <Td>{buildPartialName(order)}</Td>
                                    <Td>{order.name}</Td>
                                    <Td minW={150}>
                                        <div>
                                            {order.phone} {order.phone && order.phone.length < 10 && (
                                            <Tooltip label="This numbers has less than 10 digits"
                                                     aria-label="Phone number is too short">
                                                <Icon as={WarningIcon} color="red.500" ml={2}/>
                                            </Tooltip>
                                        )}
                                        </div>
                                    </Td>
                                    <Td>{order.county}</Td>
                                    <Td>{order.city}</Td>
                                    <Td>{order.address}</Td>
                                    <Td>{order.shipping}</Td>
                                    <Td>
                                        <Tooltip label={order.submitAttempt ? "Attempted to submit" : "Did not submit"}>
                                        <span>
                                            {order.submitAttempt ? <CheckIcon color="green.500"/> :
                                                <MdWarning color="red"/>}
                                        </span>
                                        </Tooltip>
                                    </Td>
                                    <Td>
                                        {order.phone_exists && (
                                            <Tooltip label="Phone number exists in orders">
                                                <CheckIcon color="green.500"/>
                                            </Tooltip>
                                        )}
                                    </Td>
                                    <Td textAlign="center">
                                        {order.terms_accepted === 1 ? (
                                            <Tooltip label="Terms Accepted">
                                                <span>
                                                    <CheckIcon color="green.500"/>
                                                </span>
                                            </Tooltip>
                                        ) : order.terms_accepted === 0 ? (
                                            <Tooltip label="Terms Not Accepted">
                                                <span>
                                                    <Icon as={MdWarning} color="red.500"/>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Text color="gray.400" fontWeight="bold">-</Text>
                                        )}
                                    </Td>
                                    <Td>
                                        <Tag colorScheme="blue">{order.no_of_calls}</Tag>
                                    </Td>
                                    <Td>
                                        <Menu placement="left-start" isLazy>
                                            <MenuButton
                                                as={IconButton}
                                                size="sm"
                                                variant="ghost"
                                                icon={<HiOutlineDotsVertical/>}
                                                aria-label="Actions"
                                            />

                                            <MenuList minW="180px" zIndex={20}>
                                                {canEditPartials && isPremium && (
                                                    <MenuItem icon={<MdEdit/>} onClick={() => openEditPartial(order)}>
                                                        Edit partial
                                                    </MenuItem>
                                                )}

                                                {canEditPartials && isPremium && (
                                                    <MenuItem
                                                        icon={<MdCheckCircle/>}
                                                        onClick={() => handleConfirmOrder(order)}
                                                    >
                                                        Confirm order
                                                    </MenuItem>
                                                )}

                                                {order.upsells?.length > 0 && (
                                                    <MenuItem
                                                        icon={<MdVisibility/>}
                                                        isDisabled
                                                        title={order.upsells.map((u) => u.name).join(', ')}
                                                    >
                                                        {`Upsells (${order.upsells.length})`}
                                                    </MenuItem>
                                                )}

                                                <MenuItem icon={<MdAddCall/>} onClick={() => handleAddCall(order)}>
                                                    Add call
                                                </MenuItem>

                                                {canDeletePartials && <MenuDivider/>}

                                                {canDeletePartials && (
                                                    <MenuItem
                                                        icon={<MdDelete/>}
                                                        color="red.500"
                                                        onClick={() => handleClickOpen(order)}
                                                    >
                                                        Delete partial
                                                    </MenuItem>
                                                )}
                                            </MenuList>
                                        </Menu>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
                <Flex justifyContent="space-between" mt="4" flexWrap="wrap">
                    <Text>
                        Showing {Math.min(totalOrders, 15)} of {totalOrders} orders
                    </Text>
                    <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange}/>
                </Flex>
            </Card>

            <ConfirmationDialog
                open={isOpen}
                handleClose={onClose}
                handleConfirm={handleConfirmDelete}
                title="Delete Confirmation"
                description="Are you sure you want to delete this partial?"
            />

            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size='2xl'>
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>Edit Partial Order</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <FormControl mb="4">
                            <FormLabel>Name</FormLabel>
                            <Input name="name" value={editForm.name} onChange={handleEditFormChange}/>
                        </FormControl>
                        <FormControl mb="4">
                            <FormLabel>Phone</FormLabel>
                            <Input name="phone" value={editForm.phone} onChange={handleEditFormChange}/>
                        </FormControl>
                        <FormControl mb="4">
                            <FormLabel>Address</FormLabel>
                            <Input name="address" value={editForm.address} onChange={handleEditFormChange}/>
                        </FormControl>
                        <FormControl mb="4">
                            <FormLabel>City</FormLabel>
                            <Input name="city" value={editForm.city} onChange={handleEditFormChange}/>
                        </FormControl>
                        <FormControl mb="4">
                            <FormLabel>County</FormLabel>
                            <Input name="county" value={editForm.county} onChange={handleEditFormChange}/>
                        </FormControl>
                        <FormControl mb="4">
                            <FormLabel>Option</FormLabel>
                            <Select name="option_id" value={editForm.option_id} onChange={handleEditFormChange}>
                                {editPartial?.product?.options?.map(option => (
                                    <option key={option.id} value={option.id}>
                                        {option.name} - {option.value}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {editPartial?.product?.use_advanced_options ? (() => {
                            const option = editPartial.product.options.find(
                                o => String(o.id) === String(editForm.option_id)
                            )
                            if (!option || !option.colors?.length) return null

                            const palette = option.colors
                            const ids = Array.from(
                                {length: option.quantity},
                                (_, i) => editForm.color_ids[i] || ''
                            )

                            return (
                                <FormControl mb="4" key={editForm.option_id}>
                                    <FormLabel>Colors</FormLabel>
                                    {ids.map((id, idx) => (
                                        <Flex key={idx} align="center" mb="2">
                                            <Select
                                                value={id}
                                                onChange={e => {
                                                    const next = [...ids]
                                                    next[idx] = e.target.value
                                                    setEditForm({...editForm, color_ids: next})
                                                }}
                                                placeholder="Select color"
                                                mr="2"
                                            >
                                                {palette.map(c => (
                                                    <option key={c.id} value={c.id}>
                                                        {c.color}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Flex>
                                    ))}
                                </FormControl>
                            )
                        })() : null}


                        <FormControl mb="4">
                            {editForm.upsells?.length ? <FormLabel>Upsells:</FormLabel> : null}
                            {editForm.upsells?.map((upsell, index) =>
                                <Flex
                                    key={index}
                                    align="center"
                                    justify="space-between"
                                    borderWidth="1px"
                                    borderRadius="md"
                                    p="2"
                                    mb="2"
                                >
                                    <Text>
                                        {`${upsell.name} - ${editPartial[`upsell_${upsell.id}_${upsell.upsell_id}`]} RON`}
                                    </Text>
                                    <IconButton
                                        size="sm"
                                        variant="ghost"
                                        icon={<MdDelete/>}
                                        onClick={() => {
                                            const updatedUpsells = editForm?.upsells.filter((_, i) => i !== index)
                                            setEditForm({...editForm, upsells: updatedUpsells})
                                        }}
                                        aria-label="Remove upsell"
                                    />
                                </Flex>
                            )}
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" onClick={() => setIsEditModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleSaveEditPartial} ml="3">
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </Box>
    );
};

export default PartialOrdersPage;